import { useBillRunSummary } from "@features/bx/BulkBillGeneration/hooks";
import { Check } from "lucide-react";
import { KpiStatsCard } from "../KpiStatsCard";

interface DetailsTabProps {
  batchId: string;
}

interface KpiItem {
  label: string;
  value: any;
  subtitle: string;
  trend?: {
    value: number;
    direction: "up" | "down";
    label: string;
  };
}

export const DetailsTab = ({ batchId }: DetailsTabProps) => {
  const { data: details } = useBillRunSummary(batchId);

  if (!details) return null;

  /* ---------------- Approval Items ---------------- */
  const approvalItems = [
    {
      title: "Consumer Data Accuracy",
      description:
        "All consumer account details, service information, and contact details are accurate and complete.",
    },
    {
      title: "Meter Reading Validation",
      description:
        "All meter readings are collected, validated, and anomalies have been reviewed and resolved.",
    },
    {
      title: "Tariff & Rate Application",
      description:
        "All applicable tariffs, rates, and service charges are correctly applied to consumer accounts.",
    },
    {
      title: "Bill Calculation Accuracy",
      description:
        "Bill calculations are correct and comply with applicable tariffs and regulations.",
    },
    {
      title: "Adjustments & Credits",
      description:
        "All adjustments, credits, and special charges are correctly applied to appropriate accounts.",
    },
    {
      title: "Bill Distribution Authorization",
      description:
        "Authorize distribution of bills through all configured channels (email, SMS, print, or app).",
    },
    {
      title: "Payment Processing Authorization",
      description:
        "Authorize payment processing and automatic deductions for consumers with auto-pay enabled.",
    },
    {
      title: "Consumer Communication Authorization",
      description:
        "Authorize sending billing notifications, payment reminders, and related communications.",
    },
  ];

  /* ---------------- KPI Configs ---------------- */

  const billStats: KpiItem[] = [
    {
      label: "Generated",
      value: details.billGenerationStats.generated.current,
      subtitle: `Last: ${details.billGenerationStats.generated.previous}`,
      trend: {
        value: details.billGenerationStats.generated.changePercentage,
        direction: details.billGenerationStats.generated.changeDirection,
        label: "vs last cycle",
      },
    },
    {
      label: "Actual Bills",
      value: details.billGenerationStats.actualBills.current,
      subtitle: `Last: ${details.billGenerationStats.actualBills.previous}`,
      trend: {
        value: details.billGenerationStats.actualBills.changePercentage,
        direction: details.billGenerationStats.actualBills.changeDirection,
        label: "vs last cycle",
      },
    },
    {
      label: "Estimated Bills",
      value: details.billGenerationStats.estimatedBills.current,
      subtitle: `Last: ${details.billGenerationStats.estimatedBills.previous}`,
      trend: {
        value: details.billGenerationStats.estimatedBills.changePercentage,
        direction: details.billGenerationStats.estimatedBills.changeDirection,
        label: "vs last cycle",
      },
    },
    {
      label: "Failed Bills",
      value: details.billGenerationStats.failedBills.current,
      subtitle: `Last: ${details.billGenerationStats.failedBills.previous}`,
    },
    {
      label: "Excluded Consumers",
      value: details.billGenerationStats.excludedConsumers.current,
      subtitle: `Last: ${details.billGenerationStats.excludedConsumers.previous}`,
      trend: {
        value: details.billGenerationStats.excludedConsumers.changePercentage,
        direction:
          details.billGenerationStats.excludedConsumers.changeDirection,
        label: "vs last cycle",
      },
    },
  ];

  const consumptionStats: KpiItem[] = [
    {
      label: "Total Consumption",
      value: `${details.consumptionDistribution.totalConsumption.current} ${details.consumptionDistribution.totalConsumption.unit}`,
      subtitle: `Last: ${details.consumptionDistribution.totalConsumption.previous} ${details.consumptionDistribution.totalConsumption.unit}`,
      trend: {
        value:
          details.consumptionDistribution.totalConsumption.changePercentage,
        direction:
          details.consumptionDistribution.totalConsumption.changeDirection,
        label: "vs last cycle",
      },
    },
    {
      label: "High Consumption",
      value: details.consumptionDistribution.highConsumption.current,
      subtitle: `Last: ${details.consumptionDistribution.highConsumption.previous}`,
    },
    {
      label: "Low Consumption",
      value: details.consumptionDistribution.lowConsumption.current,
      subtitle: `Last: ${details.consumptionDistribution.lowConsumption.previous}`,
    },
    {
      label: "Zero Consumption",
      value: details.consumptionDistribution.zeroConsumption.current,
      subtitle: `Last: ${details.consumptionDistribution.zeroConsumption.previous}`,
      trend: {
        value: details.consumptionDistribution.zeroConsumption.changePercentage,
        direction:
          details.consumptionDistribution.zeroConsumption.changeDirection,
        label: "vs last cycle",
      },
    },
  ];

  const financialStats: KpiItem[] = [
    {
      label: "Total Billed Amount",
      value: details.financialSummary.totalBilledAmount.current,
      subtitle: `Last: ${details.financialSummary.totalBilledAmount.previous}`,
      trend: {
        value: details.financialSummary.totalBilledAmount.changePercentage,
        direction: details.financialSummary.totalBilledAmount.changeDirection,
        label: "vs last cycle",
      },
    },
    {
      label: "Service Charges",
      value: details.financialSummary.serviceCharges.current,
      subtitle: `Last: ${details.financialSummary.serviceCharges.previous}`,
    },
    {
      label: "Outstanding Amount",
      value: details.financialSummary.outstandingAmount.current,
      subtitle: `Last: ${details.financialSummary.outstandingAmount.previous}`,
      trend: {
        value: details.financialSummary.outstandingAmount.changePercentage,
        direction: details.financialSummary.outstandingAmount.changeDirection,
        label: "vs last cycle",
      },
    },
    {
      label: "Late Payments",
      value: details.financialSummary.latePayments.current,
      subtitle: `Bills: ${details.financialSummary.latePayments.billsCount}`,
      trend: {
        value: details.financialSummary.latePayments.changePercentage,
        direction: details.financialSummary.latePayments.changeDirection,
        label: "vs last cycle",
      },
    },
  ];

  /* ---------------- Reusable Renderer ---------------- */

  const renderKpis = (items: KpiItem[], columns: number) => (
    <div className={`grid grid-cols-${columns} gap-4`}>
      {items.map((item) => (
        <KpiStatsCard key={item.label} {...item} />
      ))}
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      {/* Bill Generation statistics? */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          Bill Generation Statistics
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Overview of bills generated in this cycle
        </p>
        {renderKpis(billStats, 5)}
      </section>

      {/* Consumption Distribution */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          Consumption Distribution
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Distribution of measured usage patterns across consumers
        </p>
        {renderKpis(consumptionStats, 4)}
      </section>

      {/* Financial Summary */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          Financial Summary
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Overview of billing financial metrics and late payments
        </p>
        {renderKpis(financialStats, 5)}
      </section>

      {/* Bill Approval Consent */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          Bill Approval Consent
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Consent items verified during bill approval process
        </p>

        <div className="space-y-3">
          {approvalItems.map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
            >
              <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center mt-0.5">
                <Check className="h-3 w-3 text-white" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900">
                  {item.title}
                </h4>
                <p className="text-xs text-gray-600 mt-0.5">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
